import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Box, Breadcrumbs, Button, Chip, CircularProgress, Collapse,
  Divider, IconButton, LinearProgress, List, ListItem, ListItemText,
  Paper, Tooltip, Typography,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import AppShell from './components/AppShell';
import QnrUploadStep from './components/QnrUploadStep';
import api from './services/api';

const TYPE_COLOURS = {
  radio: 'primary',
  checkbox: 'secondary',
  'table-radio': 'info',
  'table-checkbox': 'info',
  'table-textbox': 'info',
  essay: 'warning',
  text: 'default',
  menu: 'default',
  slider: 'info',
};

// ─── Step 2: Review ───────────────────────────────────────────────────────────

const GRID_TYPES = ['table-radio', 'table-checkbox', 'table-textbox'];
const GRID_SPLIT_THRESHOLD = 4;

const willSplit = (q) => GRID_TYPES.includes(q.type) && (q.rows?.length || 0) > GRID_SPLIT_THRESHOLD;

const QA_TYPE_LABELS = {
  scale_error: 'Scale error',
  duplicate_option: 'Duplicate option',
  spelling: 'Spelling',
  truncated: 'Truncated',
  other: 'Issue',
};

const ReviewStep = ({ questions, setQuestions, qaIssues, onConfirm, onReset }) => {
  const [expanded, setExpanded] = useState(null);
  const [qaExpanded, setQaExpanded] = useState(true);

  const remove = (idx) => setQuestions((qs) => qs.filter((_, i) => i !== idx));

  const routingCount = questions.filter((q) => q.routing).length;
  const splitCount = questions.filter(willSplit).length;

  // Each question gets its own page; splits produce 2 pages
  const totalPages = questions.reduce((n, q) => n + (willSplit(q) ? 2 : 1), 0);

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="h5" fontFamily="'Work Sans', sans-serif" fontWeight={700}>
          Review Questions
        </Typography>
        <Button size="small" onClick={onReset}>Upload different file</Button>
      </Box>

      <Typography variant="body2" color="textSecondary" mb={2}>
        {questions.length} questions identified → {totalPages} pages in Alchemer (one question per page).
        Remove any you don't want scripted, then click <strong>Script to Alchemer</strong>.
      </Typography>

      {qaIssues.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ mb: 2, borderRadius: 2, borderColor: 'error.light', overflow: 'hidden' }}
        >
          <Box
            display="flex" alignItems="center" gap={1} px={2} py={1.5}
            sx={{ bgcolor: 'error.50', cursor: 'pointer', '&:hover': { bgcolor: 'error.100' } }}
            onClick={() => setQaExpanded((v) => !v)}
          >
            <BugReportRoundedIcon color="error" fontSize="small" />
            <Typography variant="body2" fontWeight={600} color="error.main" sx={{ flex: 1 }}>
              QA found {qaIssues.length} potential issue{qaIssues.length > 1 ? 's' : ''} — review before scripting
            </Typography>
            <Typography variant="caption" color="error.main">{qaExpanded ? 'Hide' : 'Show'}</Typography>
          </Box>
          <Collapse in={qaExpanded}>
            <Divider />
            {qaIssues.map((issue, i) => (
              <Box key={i} px={2} py={1} sx={{ borderBottom: i < qaIssues.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Chip label={QA_TYPE_LABELS[issue.type] || 'Issue'} size="small" color="error" variant="outlined" />
                  <Typography variant="caption" color="textSecondary" fontWeight={600}>{issue.question_id}</Typography>
                  <Typography variant="caption">{issue.detail}</Typography>
                </Box>
              </Box>
            ))}
          </Collapse>
        </Paper>
      )}

      {splitCount > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {splitCount} grid question{splitCount > 1 ? 's have' : ' has'} more than {GRID_SPLIT_THRESHOLD} rows and will be split into 2 pages each to reduce respondent fatigue.
        </Alert>
      )}
      {routingCount > 0 && (
        <Alert severity="warning" icon={<WarningAmberRoundedIcon />} sx={{ mb: 1 }}>
          {routingCount} question{routingCount > 1 ? 's have' : ' has'} routing/display conditions.
          These will be created in Alchemer but routing logic must be added manually afterwards.
        </Alert>
      )}

      <List disablePadding>
        {questions.map((q, idx) => (
          <Paper
            key={idx}
            variant="outlined"
            sx={{ mb: 1, borderRadius: 2, overflow: 'hidden' }}
          >
            <ListItem
              sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              onClick={() => setExpanded(expanded === idx ? null : idx)}
              secondaryAction={
                <Tooltip title="Remove from scripting">
                  <IconButton edge="end" size="small" onClick={(e) => { e.stopPropagation(); remove(idx); }}>
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 32 }}>
                      {q.id}
                    </Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
                      {q.title}
                    </Typography>
                    {q.alias && (
                      <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                        [{q.alias}]
                      </Typography>
                    )}
                    <Chip
                      label={q.type}
                      size="small"
                      color={TYPE_COLOURS[q.type] || 'default'}
                      variant="outlined"
                    />
                    {willSplit(q) && (
                      <Tooltip title={`${q.rows.length} rows — will be split across 2 pages`}>
                        <Chip label="split" size="small" color="info" variant="outlined" />
                      </Tooltip>
                    )}
                    {q.routing && (
                      <Tooltip title={`Routing: ${q.routing}`}>
                        <WarningAmberRoundedIcon fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                  </Box>
                }
              />
            </ListItem>

            <Collapse in={expanded === idx}>
              <Divider />
              <Box px={2} py={1.5} bgcolor="grey.50">
                {q.description && (
                  <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                    {q.description}
                  </Typography>
                )}
                {q.routing && (
                  <Typography variant="caption" color="warning.main" display="block" mb={1}>
                    Routing: {q.routing}
                  </Typography>
                )}
                {(q.rows?.length > 0) && (
                  <Box mb={1}>
                    <Typography variant="caption" fontWeight={600} display="block">Rows</Typography>
                    {q.rows.map((r, i) => (
                      <Typography key={i} variant="caption" display="block" color="textSecondary">• {r}</Typography>
                    ))}
                  </Box>
                )}
                {(q.options?.length > 0) && (
                  <Box>
                    <Typography variant="caption" fontWeight={600} display="block">
                      {q.rows?.length > 0 ? 'Columns' : 'Options'}
                    </Typography>
                    {q.options.map((o, i) => (
                      <Typography key={i} variant="caption" display="block" color="textSecondary">• {o}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Collapse>
          </Paper>
        ))}
      </List>

      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          size="large"
          disabled={questions.length === 0}
          onClick={onConfirm}
        >
          Script {questions.length} question{questions.length !== 1 ? 's' : ''} to Alchemer
        </Button>
      </Box>
    </Box>
  );
};

// ─── Logic checklist panel ────────────────────────────────────────────────────

const LogicChecklist = ({ checklist }) => {
  const [open, setOpen] = useState(false);
  const { display_logic, disqualify_logic, screenout_logic, dq_page_id, so_page_id } = checklist;
  const total = display_logic.length + disqualify_logic.length + screenout_logic.length;
  if (total === 0) return null;

  const allLines = [
    '=== DISPLAY LOGIC (show/hide conditions) ===',
    ...display_logic.map(i => `Q${i.question_id}  ${i.condition}`),
    '',
    '=== DISQUALIFY LOGIC ===',
    dq_page_id ? `Disqualify page ID: ${dq_page_id}` : 'Disqualify page: creation failed',
    ...disqualify_logic.map(i => `Q${i.question_id}  If "${i.option}" → skip to Disqualify page`),
    '',
    '=== SCREENOUT LOGIC ===',
    so_page_id ? `Screenout page ID: ${so_page_id}` : 'Screenout page: creation failed',
    ...screenout_logic.map(i => `Q${i.question_id}  If "${i.option}" → skip to Screenout page`),
    '',
    '=== FINAL SKIP (add to last main question) ===',
    'Condition: Always TRUE → skip to Complete page (bypasses DQ/SO pages for good respondents)',
  ].join('\n');

  return (
    <Paper variant="outlined" sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
      <Box
        display="flex" alignItems="center" gap={1} px={2} py={1.5}
        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
        onClick={() => setOpen(v => !v)}
      >
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
          Logic checklist — {total} item{total !== 1 ? 's' : ''} to add manually in Alchemer
        </Typography>
        <Button
          size="small"
          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(allLines); }}
        >
          Copy all
        </Button>
        <Typography variant="caption" color="textSecondary">{open ? 'Hide' : 'Show'}</Typography>
      </Box>
      <Collapse in={open}>
        <Divider />
        {display_logic.length > 0 && (
          <Box px={2} pt={1.5} pb={0.5}>
            <Typography variant="caption" fontWeight={700} color="textSecondary" display="block" mb={0.5}>
              DISPLAY LOGIC
            </Typography>
            {display_logic.map((item, i) => (
              <Box key={i} display="flex" gap={1} mb={0.5}>
                <Typography variant="caption" color="textSecondary" sx={{ minWidth: 36 }}>Q{item.question_id}</Typography>
                <Typography variant="caption">{item.condition}</Typography>
              </Box>
            ))}
          </Box>
        )}
        {disqualify_logic.length > 0 && (
          <Box px={2} pt={1} pb={0.5}>
            <Typography variant="caption" fontWeight={700} color="error" display="block" mb={0.5}>
              DISQUALIFY LOGIC{dq_page_id ? ` — page ${dq_page_id}` : ''}
            </Typography>
            {disqualify_logic.map((item, i) => (
              <Box key={i} display="flex" gap={1} mb={0.5}>
                <Typography variant="caption" color="textSecondary" sx={{ minWidth: 36 }}>Q{item.question_id}</Typography>
                <Typography variant="caption">If <strong>"{item.option}"</strong> → skip to Disqualify page</Typography>
              </Box>
            ))}
          </Box>
        )}
        {screenout_logic.length > 0 && (
          <Box px={2} pt={1} pb={0.5}>
            <Typography variant="caption" fontWeight={700} color="warning.main" display="block" mb={0.5}>
              SCREENOUT LOGIC{so_page_id ? ` — page ${so_page_id}` : ''}
            </Typography>
            {screenout_logic.map((item, i) => (
              <Box key={i} display="flex" gap={1} mb={0.5}>
                <Typography variant="caption" color="textSecondary" sx={{ minWidth: 36 }}>Q{item.question_id}</Typography>
                <Typography variant="caption">If <strong>"{item.option}"</strong> → skip to Screenout page</Typography>
              </Box>
            ))}
          </Box>
        )}
        <Box px={2} pt={1} pb={1.5} sx={{ bgcolor: 'grey.50' }}>
          <Typography variant="caption" fontWeight={700} color="textSecondary" display="block" mb={0.5}>
            FINAL SKIP — add to last main question
          </Typography>
          <Typography variant="caption">
            Condition: <strong>Always TRUE</strong> → skip to Complete page (bypasses DQ/SO pages for good respondents)
          </Typography>
        </Box>
      </Collapse>
    </Paper>
  );
};

// ─── Step 3: Results ──────────────────────────────────────────────────────────

const ResultsStep = ({ results, onReset }) => {
  const { created, failed, sliders_skipped, routing_flagged, results: rows, logic_checklist } = results;

  return (
    <Box>
      <Typography variant="h5" fontFamily="'Work Sans', sans-serif" fontWeight={700} mb={2}>
        Scripting Complete
      </Typography>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Paper variant="outlined" sx={{ px: 3, py: 2, borderRadius: 2, textAlign: 'center', minWidth: 120 }}>
          <Typography variant="h4" fontWeight={700} color="success.main">{created}</Typography>
          <Typography variant="body2" color="textSecondary">Created</Typography>
        </Paper>
        {sliders_skipped > 0 && (
          <Paper variant="outlined" sx={{ px: 3, py: 2, borderRadius: 2, textAlign: 'center', minWidth: 120 }}>
            <Typography variant="h4" fontWeight={700} color="info.main">{sliders_skipped}</Typography>
            <Typography variant="body2" color="textSecondary">Sliders — add manually</Typography>
          </Paper>
        )}
        {failed > 0 && (
          <Paper variant="outlined" sx={{ px: 3, py: 2, borderRadius: 2, textAlign: 'center', minWidth: 120 }}>
            <Typography variant="h4" fontWeight={700} color="error.main">{failed}</Typography>
            <Typography variant="body2" color="textSecondary">Failed</Typography>
          </Paper>
        )}
        {routing_flagged > 0 && (
          <Paper variant="outlined" sx={{ px: 3, py: 2, borderRadius: 2, textAlign: 'center', minWidth: 120 }}>
            <Typography variant="h4" fontWeight={700} color="warning.main">{routing_flagged}</Typography>
            <Typography variant="body2" color="textSecondary">Routing to add manually</Typography>
          </Paper>
        )}
      </Box>

      {sliders_skipped > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          {sliders_skipped} slider question{sliders_skipped > 1 ? 's have' : ' has'} been given a blank page — add the slider question manually in Alchemer.
        </Alert>
      )}
      {routing_flagged > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {routing_flagged} question{routing_flagged > 1 ? 's have' : ' has'} routing conditions that need to be configured manually in Alchemer.
        </Alert>
      )}

      <List disablePadding>
        {rows.map((r, idx) => (
          <Paper key={idx} variant="outlined" sx={{ mb: 1, borderRadius: 2 }}>
            <ListItem>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    {r.slider_skip
                      ? <Chip label="add slider" size="small" color="info" />
                      : r.success
                        ? <CheckCircleRoundedIcon fontSize="small" color="success" />
                        : <ErrorRoundedIcon fontSize="small" color="error" />
                    }
                    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 32 }}>{r.id}</Typography>
                    <Typography variant="body2" sx={{ flex: 1 }}>{r.title}</Typography>
                    {r.has_routing && (
                      <Tooltip title="Has routing — add manually in Alchemer">
                        <WarningAmberRoundedIcon fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                  </Box>
                }
                secondary={r.error ? <Typography variant="caption" color="error">{r.error}</Typography> : null}
              />
            </ListItem>
          </Paper>
        ))}
      </List>

      {logic_checklist && <LogicChecklist checklist={logic_checklist} />}

      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button variant="outlined" onClick={onReset}>Script another document</Button>
      </Box>
    </Box>
  );
};

// ─── Scripting progress overlay ───────────────────────────────────────────────

const ScriptingProgress = ({ total, done }) => (
  <Box textAlign="center" py={6}>
    <CircularProgress size={48} sx={{ mb: 2 }} />
    <Typography variant="body1" fontWeight={500} mb={1}>Scripting questions into Alchemer…</Typography>
    <LinearProgress variant="determinate" value={(done / total) * 100} sx={{ maxWidth: 300, mx: 'auto', borderRadius: 1 }} />
    <Typography variant="body2" color="textSecondary" mt={1}>{done} / {total}</Typography>
  </Box>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const SurveyScripting = () => {
  const { surveyId } = useParams();
  const [step, setStep] = useState('upload'); // upload | review | scripting | results
  const [questions, setQuestions] = useState([]);
  const [scriptingProgress, setScriptingProgress] = useState({ done: 0, total: 0 });
  const [scriptResults, setScriptResults] = useState(null);
  const [scriptError, setScriptError] = useState(null);
  const [qaIssues, setQaIssues] = useState([]);
  const pollTimer = useRef(null);

  const handleParsed = (qs, issues) => {
    setQuestions(qs);
    setQaIssues(issues || []);
    setStep('review');
  };

  const handleScript = async () => {
    setScriptingProgress({ done: 0, total: questions.length });
    setStep('scripting');
    setScriptError(null);

    let taskId;
    try {
      const { data } = await api.post(`/surveys/${surveyId}/script-questions/`, { questions });
      taskId = data.task_id;
    } catch (err) {
      setScriptError(err.response?.data?.error || 'Scripting failed. Please try again.');
      setStep('review');
      return;
    }

    const poll = async () => {
      try {
        const { data } = await api.get(`/surveys/${surveyId}/script-status/${taskId}/`);
        if (data.state === 'success') {
          setScriptResults(data);
          setStep('results');
        } else if (data.state === 'failure') {
          setScriptError(data.error || 'Scripting failed. Please try again.');
          setStep('review');
        } else {
          setScriptingProgress({ done: data.done || 0, total: data.total || questions.length });
          pollTimer.current = setTimeout(poll, 2000);
        }
      } catch {
        setScriptError('Lost connection during scripting. Please try again.');
        setStep('review');
      }
    };

    pollTimer.current = setTimeout(poll, 1500);
  };

  const reset = () => {
    clearTimeout(pollTimer.current);
    setStep('upload');
    setQuestions([]);
    setScriptResults(null);
    setScriptError(null);
  };

  return (
    <AppShell>
      <Breadcrumbs separator=">" sx={{ mb: 4 }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>Home</Link>
        <Link to={`/survey/${surveyId}`} style={{ textDecoration: 'none', color: 'inherit' }}>Survey</Link>
        <Typography color="textPrimary">Survey Scripting</Typography>
      </Breadcrumbs>

      <Box maxWidth={800} mx="auto">
        {scriptError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScriptError(null)}>
            {scriptError}
          </Alert>
        )}

        {step === 'upload' && (
          <QnrUploadStep
            surveyId={surveyId}
            onParsed={handleParsed}
            heading="Survey Scripting"
            subtitle="Upload your questionnaire document. Claude will identify all questions and prepare them for scripting into Alchemer."
          />
        )}
        {step === 'review' && (
          <ReviewStep
            questions={questions}
            setQuestions={setQuestions}
            qaIssues={qaIssues}
            onConfirm={handleScript}
            onReset={reset}
          />
        )}
        {step === 'scripting' && (
          <ScriptingProgress total={scriptingProgress.total} done={scriptingProgress.done} />
        )}
        {step === 'results' && scriptResults && (
          <ResultsStep results={scriptResults} onReset={reset} />
        )}
      </Box>
    </AppShell>
  );
};

export default SurveyScripting;
